import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Button from 'components/common/Button/Button'
import Input from 'components/common/Input/Input'
import Link from 'components/common/Link/Link'
import Eye from 'components/assets/icons/Eye'
import ResponseMessage from 'components/common/ResponseMessage/ResponseMessage'
import { emailValidation, passwordValidation } from 'constants/inputValidation'
import api from 'helpers/api'
import { parseToken } from 'helpers/auth'
import style from 'styles/auth.module.scss'
import Loader from 'components/assets/icons/Loader'
import LoginLayout from 'components/Auth/LoginLayout/LoginLayout'
import { Field, SubmitButton, AuthLink, AuthMessage, AuthAlt } from 'components/Auth/LoginLayout/fields'
import { isNewClientLogin } from 'config/loginDesign'

const SignUp = () => {
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [loading, setLoading] = useState(false)

  const { t, i18n: { language } } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token')
  const data = token ? parseToken(token) : null

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    trigger
  } = useForm({
    criteriaMode: 'all',
    delayError: 0,
    defaultValues: {
      email: data?.email,
      password: ''
    },
    mode: 'all',
  })

  useEffect(() => {
    trigger()
  }, [language, trigger])

  const togglePasswordVisibility = () => {
    setIsPasswordShown(!isPasswordShown)
  }

  const onSubmit = handleSubmit(async data => {
    setLoading(true)

    const register = async () => {
      try {
        const res = await api.post({ url: '/auth/clients/signup', data })

        if (res.success && res.response.confirmToken) {
          navigate('/sign-up-confirm?token='+res.response.confirmToken)
        } else {
          setErrorMessage('failed')
        }
      } catch (error) {
        setErrorMessage(error.message)
        if (error.message === 'Email is already registered'){
          setTimeout( () => {
            navigate('/sign-in')
          }, 5000)
        }
      }
      setLoading(false)
    }

    setTimeout( () => register(), 500)
  })

  const messageSelection = () => {
    let message = ''
    switch (errorMessage){
      case 'Email is already registered':
        message = t('main:auth.signUp.knownEmail')
        break;
      case 'Client does not exist':
        message = t('main:auth.signUp.unknownEmail')
        break;
      case 'Password should contain at least 9 characters, including 1 digit and 1 capital letter':
        message = t('main:auth.signUp.wrongPassword')
        break;
      case 'Too many requests':
        message = t('main:auth.confirm.tooManyRequests')
        break;
      default:
        message = t('main:errorMessages.general')
        break;
    }
    return message
  }


  if (isNewClientLogin()) {
    return (
      <LoginLayout title={t('main:auth.signUp.title')}>
        {errorMessage && <AuthMessage>{messageSelection()}</AuthMessage>}
        <form onSubmit={onSubmit} noValidate>
          <Field
            id="signup-email"
            type="email"
            label="Email"
            autoComplete="email"
            placeholder={`${t('common:your')} ${t('common:form.email.placeholder')}`}
            register={register('email', emailValidation(t))}
            error={errors.email?.message}
          />
          <Field
            id="signup-password"
            type="password"
            label={t('main:auth.password.password')}
            autoComplete="new-password"
            placeholder={t('main:auth.password.placeholder')}
            register={register('password', passwordValidation(t))}
            error={errors.password && Object.values(errors.password.types).map((error, id) => <div key={'err' + id}>{error}</div>)}
          />
          <SubmitButton label={t('main:auth.signUp.register')} loading={loading} disabled={!isValid} />
        </form>
        <AuthAlt><AuthLink to="/sign-in" label={t('main:auth.signUp.alreadyHaveAccount')} /></AuthAlt>
      </LoginLayout>
    )
  }

  return (
    <>
      <h2 className={style.title}>{t('main:auth.signUp.title')}</h2>

      {errorMessage &&
        <ResponseMessage
          className={style['response-message']}
          label={messageSelection()}
        />
      }

      <form onSubmit={onSubmit} className={style.form}>

        <Input
          type="email"
          label="Email"
          placeholder={`${t('common:your')} ${t('common:form.email.placeholder')}`}
          register={register('email', emailValidation(t))}
          darkBg
        />
        {errors.email &&
          <div className={style['input-error']}>
            {errors.email.message}
          </div>
        }


        <Input
          type={isPasswordShown ? 'text' : 'password'}
          label={t('main:auth.password.password')}
          placeholder={t('main:auth.password.placeholder')}
          register={register('password', passwordValidation(t))}
          suffix={<Eye className="clickable" />}
          onSuffixClick={togglePasswordVisibility}
          className={style.margin}
          darkBg
        />
        {errors.password &&
          <div className={style['input-error']}>
            <p>{t('main:auth.password.required')}</p>
            {Object.values(errors.password.types).map( (error, id) => {
              return <div key={'err'+id}>{error}</div>
            })}
          </div>
        }

        <Button
          type={loading ? 'loading' : 'primary'}
          label={loading ? <Loader/> : t('main:auth.signUp.register')}
          className={style.button}
          onClick={handleSubmit}
          disabled={!isValid || loading} 
        />

      </form>

      <Link
        className={style.link}
        label={t('main:auth.signUp.alreadyHaveAccount')}
        to={'/sign-in'}
        darkBg
      />
    </>
  )
}

export default SignUp
